// La ficha de una prenda, abrible desde cualquier pantalla.
//
// POR QUÉ EXISTE
//
// Editar una prenda solo se podía desde "Mi armario". Si estabas
// mirando una sugerencia y querías corregirle la estación a una camisa,
// había que salir, ir al armario, buscarla y recién ahí editarla — y al
// volver, las 24 sugerencias que estabas mirando ya no eran las mismas.
//
// Este componente es esa ficha, sin dueño. Recibe un id, se trae la
// prenda sola y devuelve el control a quien lo abrió.
//
// SE PIDE LA PRENDA DE NUEVO, aunque quien lo abre ya tenga algo
// parecido. Las pantallas manejan versiones distintas del mismo dato:
// la de outfits trae lo que devuelve el motor (sin notas, sin talla),
// la valija trae menos todavía. Editar sobre una versión recortada
// guardaría campos vacíos encima de datos buenos.

import { useEffect, useState } from "react";
import * as api from "./api";
import { cargarOpciones } from "./opciones";
import EditarPrenda from "./EditarPrenda";
import { fotoChica } from "./foto";
import { t, tError, useIdioma } from "./i18n";
import { useFocoAtrapado } from "./foco";

export default function FichaPrenda({ prendaId, onCerrar, onGuardada }) {
  // Ver src/foco.js (BA-14).
  const caja = useFocoAtrapado();
  /* Se repinta cuando llega el idioma. Sin esto el componente depende
   * de que su padre se repinte por otra razón — hoy pasa, y por eso el
   * bug del 2026-08-25 se veía en unas pantallas y no en otras. */
  useIdioma();
  const [prenda, setPrenda] = useState(null);
  const [opciones, setOpciones] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let vivo = true;

    Promise.all([api.leerPrenda(prendaId), cargarOpciones()])
      .then(([d, o]) => {
        if (!vivo) return;
        setPrenda(d.prenda);
        setOpciones(o);
      })
      .catch((e) => vivo && setError(tError(e)));

    return () => {
      vivo = false;
    };
  }, [prendaId]);

  // Escape cierra, igual que en el visor y en el editor del armario.
  // Tres modales que se cierran distinto se sienten como tres apps.
  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [onCerrar]);

  /* "Prenda fichada" NO es un campo del formulario, y es a propósito.
   *
   * El formulario de abajo se guarda con un botón y CIERRA la ficha.
   * Esto es una marca de un clic que se pone mientras mirás la prenda,
   * y cerrarte la pantalla por marcarla sería castigarte por usarla.
   * Por eso guarda solo, en el acto, y la ficha se queda abierta.
   *
   * Optimista: la marca cambia antes de que el servidor conteste. Si
   * falla se revierte y se dice. Registrar que algo se mostró no es una
   * operación que valga hacer esperar a nadie. */
  async function alternarFichada(nueva) {
    if (!prenda) return;
    setPrenda((p) => ({ ...p, fichada: nueva }));
    try {
      await api.editarPrenda(prendaId, { fichada: nueva });
      // La pantalla de atrás muestra la misma marca en la grilla.
      onGuardada?.();
    } catch (e) {
      // Se revierte y se relanza: el error lo dibuja el formulario,
      // que es donde vive el botón desde el 2026-08-26.
      setPrenda((p) => ({ ...p, fichada: !nueva }));
      throw e;
    }
  }

  async function guardar(cambios) {
    await api.editarPrenda(prendaId, cambios);
    // Se avisa hacia arriba ANTES de cerrar: la pantalla de atrás tiene
    // que refrescarse, porque cambiarle la estación a una prenda cambia
    // qué combinaciones existen.
    onGuardada?.();
    onCerrar();
  }

  return (
    <div
      className="modal modal-sobre-visor"
      role="dialog"
      aria-modal="true"
      aria-label={prenda ? `Ficha de ${prenda.nombre}` : t("ficha.titulo", "Ficha de prenda")}
      // La ficha se abre ENCIMA del visor. El clic afuera cierra solo la
      // ficha y deja el visor abierto, que es de donde viniste.
      onClick={onCerrar}
    >
      <div className="modal-caja" ref={caja} onClick={(e) => e.stopPropagation()}>
        <div className="modal-cabecera">
          {fotoChica(prenda) && (
            <img loading="lazy" decoding="async" src={fotoChica(prenda)} alt="" className="modal-foto" />
          )}
          <h2 className="modal-titulo">{prenda?.nombre || t("comun.cargando", "Cargando...")}</h2>
          <button
            className="modal-cerrar"
            onClick={onCerrar}
            aria-label={t("ficha.cerrar", "Cerrar la ficha")}
          >
            ×
          </button>
        </div>

        <div className="modal-cuerpo">
          {error && <div className="mensaje-error" role="alert">{error}</div>}
          {!prenda && !error && <p className="tenue">{t("ficha.cargando", "Cargando la ficha...")}</p>}
          {prenda && opciones && (
            <EditarPrenda
              prenda={prenda}
              opciones={opciones}
              onGuardar={guardar}
              onCancelar={onCerrar}
              onFichada={alternarFichada}
            />
          )}
        </div>
      </div>
    </div>
  );
}
